import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { ReactComponent as LinkedinIcon } from "Assets/svg/linkedInOutlined.svg";
import LinkedInChat, { OwnerChip } from "Components/common/LinkedInChat";
import inboxV2Store from "./inboxV2Store";
import {
  recordProductEvent,
  COPILOT_EVENTS
} from "Pages/Dashboard/Copilot/agent/copilotTelemetry";

const LinkedInThread = observer((props = {}) => {
  const { conversation = {} } = props || {};
  const [text, setText] = useState("");

  const {
    messages = [],
    canViewLiReplies = true,
    isViewerOwner = false,
    ownerName = "",
    ownerProfileUrl = "",
    profilePictureUrl = "",
    liConnected = true
  } = inboxV2Store?.liReply || {};
  const loading = inboxV2Store?.liReplyLoading;
  const sending = inboxV2Store?.liSending;

  const contact = inboxV2Store?.selectedContact || {};
  const contactId = inboxV2Store.getContactId(contact);
  const isOldLinkedInAccount = contact?.isOldLinkedInAccount || false;

  useEffect(() => {
    if (contactId) {
      inboxV2Store.getLinkedInReply(contactId, contact?.linkedinAccountId || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, conversation?.id]);

  const isOwner = isViewerOwner;
  const canReply = !isOldLinkedInAccount && canViewLiReplies && liConnected;
  const blockReason = !canViewLiReplies
    ? "You don’t have permission to reply to this conversation. Only the owner of the connected LinkedIn account can reply."
    : isOldLinkedInAccount
      ? "Replies aren’t available for this LinkedIn account."
      : !liConnected
        ? isOwner
          ? (recordProductEvent(COPILOT_EVENTS.LINKEDIN_DISCONNECTED),
            "Your LinkedIn account is disconnected. Reconnect to continue messaging.")
          : `${ownerName}’s LinkedIn account is disconnected. Ask ${ownerName} to reconnect.`
        : "";

  const send = async () => {
    if (!canReply) return;
    const ok = await inboxV2Store.sendLinkedInReply(text);
    if (ok) setText("");
  };

  return (
    <div className="inboxV2ThreadPane inboxV2LiPane">
      <div className="inboxV2LiCard">
      <div className="inboxV2LiHead">
        <span className="inboxV2LiHeadIcon">
          <LinkedinIcon />
        </span>
        <div className="inboxV2LiHeadMeta">
          <div className="inboxV2LiHeadTitle">LinkedIn conversation</div>
        </div>
        <button
          type="button"
          className="inboxV2Close"
          onClick={() => inboxV2Store.clearSelection()}
        >
          <i className="material-icons">close</i>
        </button>
      </div>

      <LinkedInChat
        messages={messages}
        loading={loading}
        canViewLiReplies={canViewLiReplies}
        liConnected={liConnected}
        isOwner={isOwner}
        ownerName={ownerName}
        ownerProfileUrl={ownerProfileUrl}
        profilePictureUrl={profilePictureUrl}
        step={conversation?.step || ""}
      />

      {/* Chat composer, always visible. */}
      <div className="inboxV2LiComposer">
        {canReply && !isOwner && ownerName && (
          <div className="inboxV2LiComposerNote">
            You're replying as{" "}
            <OwnerChip
              ownerName={ownerName}
              ownerProfileUrl={ownerProfileUrl}
              profilePictureUrl={profilePictureUrl}
            />
          </div>
        )}
        {!loading && !canReply && blockReason && (
          <div className="inboxV2LiBlockReason">{blockReason}</div>
        )}
        <div className="inboxV2LiInputBox">
          <textarea
            value={text}
            rows={2}
            placeholder={
              canReply ? "Write a message..." : "You can’t reply to this conversation"
            }
            disabled={!canReply || sending}
            onChange={event => setText(event.target.value)}
            onKeyDown={event => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                send();
              }
            }}
          />
          <div className="inboxV2LiComposerFooter">
            <span className="inboxV2LiComposerHint">
              Press Enter to send · Shift + Enter for a new line
            </span>
            <button
              type="button"
              className="inboxV2LiSendBtn"
              disabled={!canReply || sending || !text.trim()}
              onClick={send}
            >
              {sending ? "Sending..." : "Send"}
              <i className="material-icons">send</i>
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
});

export default LinkedInThread;
